import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getInitialJwt } from '@/utils/urlParams';
import { authenticateWithToken, getCurrentUser } from '@/api/beatlyApi';
import { COWORK_API_URL } from '@/cowork/live/coworkClient';
import { redirectToCollabWithSession } from '@/utils/collabRedirect';
import {
  getTokenFromStorage,
  getUserFromStorage,
  updateAuthInStorage,
  clearAuthFromStorage,
  clearAdminSessionFromStorage,
  isAdminShapedToken,
  logout,
} from '@/utils/auth';
import { showError } from '@/utils/alert';
import { AuthContext } from '@/contexts/AuthContext';
import type { AuthUser } from '@/utils/auth';

const AUTH_ROUTES = ['/login', '/signup'];
/** Public surfaces — rendered without any session (the backend validates a share token instead). */
// /sound/<id>: a library track's public page — opened by creators from briefs and by old adstudios.beatly.com links.
const PUBLIC_ROUTES = ['/share/', '/sound/', '/m/review/'];
// Exact public pages (query strings like `/library?playlist=` don't reach the pathname): /library is Beatly's library,
// the default music link on every brief. Exact so no other path starting with "/library" becomes public.
const PUBLIC_PAGES = ['/library'];
function isPublicRoute(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return PUBLIC_PAGES.includes(path) || PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
}

function isAuthRoute(pathname: string): boolean {
  return AUTH_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

/** Path + query after initUrlParams stripped `jwt` (handoff target for Collab redirect). */
function getPathAndSearchForCollabHandoff(): string {
  return `${window.location.pathname}${window.location.search}`;
}

async function resolveSessionToken(
  urlJwt: string | null,
  storedToken: string | undefined,
): Promise<string> {
  if (urlJwt) {
    const { token } = await authenticateWithToken(urlJwt);
    return token;
  }
  if (storedToken) {
    return storedToken;
  }
  throw new Error('No token');
}

/** team_id claim of a JWT, unverified — only compared, never trusted. */
function teamClaim(token: string | undefined): string | undefined {
  if (!token) return undefined;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.team_id === 'string' ? payload.team_id : undefined;
  } catch { return undefined; }
}

/**
 * The token the session should run on. A hybrid staff login carries the HOME workspace in its claim while the
 * platform treats another workspace as active; the datalake (queried straight from the browser) and every
 * team-scoped endpoint read the claim, so such a session saw the wrong workspace's rows or none. The with-token
 * exchange returns a token whose claim is the active workspace — that one is stored. Same-claim tokens pass through.
 */
async function workspaceToken(stored: string): Promise<{ token: string; rotated: boolean }> {
  try {
    const { token } = await authenticateWithToken(stored);
    const rotated = !!token && token !== stored && teamClaim(token) !== teamClaim(stored);
    if (!rotated) console.debug(`[auth] session token kept: exchange claim ${teamClaim(token) || '(none)'} vs stored claim ${teamClaim(stored) || '(none)'}${token === stored ? ' (same token returned)' : ''}`);
    return { token: rotated ? token : stored, rotated };
  } catch {
    return { token: stored, rotated: false };
  }
}

/** Validate an admin token via the cowork backend's /v1/me — its auth layer
 *  verifies admin tokens remotely and its CORS admits our origins, unlike
 *  api-v2's BO endpoints. The echoed identity becomes the AuthUser. */
async function resolveAdminSession(token: string): Promise<{ token: string; user: AuthUser }> {
  if (!COWORK_API_URL) throw new Error('cowork backend not configured');
  const res = await fetch(`${COWORK_API_URL}/v1/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`admin validation failed: ${res.status}`);
  const data = (await res.json()) as { user?: { user_id?: string; name?: string; email?: string; first_name?: string; last_name?: string } };
  if (!data.user?.user_id) throw new Error('no identity in response');
  const u = data.user;
  return {
    token,
    user: {
      user_id: u.user_id,
      name: u.name || u.email || u.user_id,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      // BO staff run the agent platform — the cowork surfaces ARE their job.
      show_cowork: true,
      show_evolve: true,
    } as AuthUser,
  };
}

/**
 * Bootstraps auth: if `initUrlParams()` captured a signup/magic `jwt` query param, exchanges it
 * via POST /app/v2/authenticate/with-token first (creates the account and redeems invites),
 * then validates with GET /app/v2/user. Otherwise uses the stored session token as today.
 */
export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'redirect'>('loading');
  const [user, setUser] = useState<AuthUser | undefined>(() => getUserFromStorage());

  // Bootstrap effect: validates the token once on mount. Captures the initial
  // pathname only to decide whether to land on /login or /. Subsequent in-app
  // navigations must NOT re-run this (or they would re-fetch GET /app/v2/user).
  //
  // The `cancelled` flag is the React-recommended primitive for StrictMode's
  // dev-only mount/cleanup/remount cycle: the first mount's stale promise
  // discards its setStates, the second mount's setStates land. Production
  // (no StrictMode in prod builds) runs the bootstrap exactly once.
  //
  // Every branch that keeps the user on Omni ends in `setStatus('authenticated')` because the effect
  // never re-runs after a `navigate(...)`, so it owns the final transition out of the 'loading' splash.
  // URL-JWT handoff to Collab uses a full-page redirect and skips setStatus (page unloads).
  useEffect(() => {
    let cancelled = false;
    const initialPathname = pathname;

    async function run() {
      if (isPublicRoute(initialPathname)) { setStatus('authenticated'); return; }
      // Dev-only bypass for local preview without real backend.
      // Enabled when VITE_DEV_AUTH_BYPASS=true and `__dev_bypass_user` is in localStorage.
      if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true') {
        const bypassUser = localStorage.getItem('__dev_bypass_user');
        if (bypassUser) {
          try {
            const u = JSON.parse(bypassUser) as AuthUser;
            if (cancelled) return;
            setUser(u);
            if (isAuthRoute(pathname)) {
              setStatus('redirect');
              navigate('/', { replace: true });
            } else {
              setStatus('authenticated');
            }
            return;
          } catch { /* fall through */ }
        }
      }

      const urlJwt = getInitialJwt();
      const storedToken = getTokenFromStorage();

      // BO admin sessions (embed handoff, or this tab's own admin session) take
      // their own validation path — the client endpoints reject admin tokens.
      // A token in the link always wins over the stored session. bo.* and omni.* are the same site, so an admin
      // session is kept per tab/frame (sessionStorage, see utils/auth) and is never read back from the shared
      // localStorage: a stale one there is dropped on read and this tab simply lands on the login screen.
      const adminCandidate = isAdminShapedToken(urlJwt) ? urlJwt : !urlJwt && isAdminShapedToken(storedToken) ? storedToken! : null;
      if (adminCandidate) {
        try {
          const { token, user } = await resolveAdminSession(adminCandidate);
          if (cancelled) return;
          updateAuthInStorage({ token, user });
          setUser(user);
          if (isAuthRoute(initialPathname)) navigate('/', { replace: true });
          setStatus('authenticated');
        } catch {
          if (cancelled) return;
          // Only the per-tab admin session goes; the staff member's own sign-in (shared storage) is not touched.
          clearAdminSessionFromStorage();
          setUser(undefined);
          showError('Back-office session could not be verified. Reload the page in the BO.');
          navigate('/login', { replace: true });
          setStatus('authenticated');
        }
        return;
      }

      if (isAuthRoute(initialPathname)) {
        if (!urlJwt && !storedToken) {
          setStatus('authenticated');
          return;
        }
        try {
          const sessionToken = await resolveSessionToken(urlJwt, storedToken);
          const user = await getCurrentUser(sessionToken);
          if (cancelled) return;
          if (urlJwt && user.is_influencer) {
            redirectToCollabWithSession(getPathAndSearchForCollabHandoff(), sessionToken);
            return;
          }
          updateAuthInStorage({ token: sessionToken, user });
          setUser(user);
          navigate('/', { replace: true });
          setStatus('authenticated');
          return;
        } catch {
          if (cancelled) return;
          clearAuthFromStorage();
          setUser(undefined);
          setStatus('authenticated');
          return;
        }
      }

      if (!urlJwt && !storedToken) {
        navigate('/login', { replace: true });
        setStatus('authenticated');
        return;
      }

      // A stored session with a stored user: render at once and validate in the background. The profile call
      // (GET /app/v2/user) takes seconds on api-v2 and used to hold the whole app behind the splash on every
      // refresh; a stale profile for a moment is fine, an invalid token still signs out.
      const storedUser = getUserFromStorage();
      if (!urlJwt && storedToken && storedUser?.user_id) {
        setUser(storedUser);
        setStatus('authenticated');
        const t0 = performance.now();
        workspaceToken(storedToken).then(async ({ token, rotated }) => {
          if (rotated) {
            // Every later call (datalake, api-v2) must carry the active-workspace token; queries that already
            // ran on the old claim are refetched.
            updateAuthInStorage({ token });
            console.debug(`[auth] session token rotated to the active workspace (${teamClaim(token)})`);
          }
          const fresh = await getCurrentUser(token);
          if (cancelled) return;
          console.debug(`[auth] profile refreshed in ${Math.round(performance.now() - t0)} ms`);
          updateAuthInStorage({ token, user: fresh });
          setUser(fresh);
          if (rotated) {
            // Session-scoped data only — avoid invalidating every query in the app.
            void queryClient.invalidateQueries({ queryKey: ['user'] });
            void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
            void queryClient.invalidateQueries({ queryKey: ['contenthub'] });
            void queryClient.invalidateQueries({ queryKey: ['cowork'] });
            void queryClient.invalidateQueries({ queryKey: ['datalake'] });
          }
        }).catch((err: unknown) => {
          if (cancelled) return;
          const status = err && typeof err === 'object' && 'status' in err ? Number((err as { status: unknown }).status) : undefined;
          console.debug(`[auth] profile refresh failed after ${Math.round(performance.now() - t0)} ms (status ${status ?? 'n/a'})`);
          // Only a rejected token ends the session; a transient failure keeps the cached one.
          if (status === 401 || status === 403) {
            clearAuthFromStorage();
            setUser(undefined);
            showError('Your session has expired. Please sign in again.');
            navigate('/login', { replace: true });
          }
        });
        return;
      }

      try {
        const exchanged = await resolveSessionToken(urlJwt, storedToken);
        const sessionToken = urlJwt ? exchanged : (await workspaceToken(exchanged)).token;
        const user = await getCurrentUser(sessionToken);
        if (cancelled) return;
        if (urlJwt && user.is_influencer) {
          redirectToCollabWithSession(getPathAndSearchForCollabHandoff(), sessionToken);
          return;
        }
        updateAuthInStorage({ token: sessionToken, user });
        setUser(user);
        setStatus('authenticated');
      } catch {
        if (cancelled) return;
        clearAuthFromStorage();
        setUser(undefined);
        showError('Sign-in link expired or invalid. Please try again.');
        navigate('/login', { replace: true });
        setStatus('authenticated');
      }
    }

    run();
    return () => {
      cancelled = true;
    };
    // Intentionally mount-only: pathname/navigate are read once via initialPathname.
  }, []);

  // Pathname watcher: bounce already-authenticated users off /login or /signup
  // without re-validating the token. No network call.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (!user) return;
    if (isAuthRoute(pathname)) {
      navigate('/', { replace: true });
    }
  }, [pathname, user, status, navigate]);

  if (status === 'redirect') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="text-[#667085] text-[15px]">Redirecting to sign in…</div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="text-[#667085] text-[15px]">Signing you in…</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
